import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SuccessResponse, ErrorResponse } from '../models/responses.interface';
import { ERROR_CODE_MAP } from '../utils/error-code-map';
import { OrganizationRequest } from '../models/organization-request.interface';
import {
  OrganizationResponse,
  GenerateSDKKeysResponse,
  GenerateAPIKeyResponse,
} from '../models/organization-response.interface';
import { environment } from 'src/environments/environment';

@Injectable()
export class OrganizationService {
  private readonly baseUrl = `${environment.BRIDGEPAY_BASE}/v1`;

  constructor(private http: HttpClient) {}

  /**
   * Create a paypoint organization for a company.
   * @param payload - organization  details payload.
   * @returns Observable of SuccessResponse containing OrganizationResponse.
   */
  createOrganization(
    payload: OrganizationRequest,
  ): Observable<SuccessResponse<OrganizationResponse>> {
    return this.http
      .post<SuccessResponse<OrganizationResponse>>(`${this.baseUrl}/admin/organization/`, payload)
      .pipe(catchError((error) => this.handleError(error)));
  }

  getDefaultOrganization(companyId: string): Observable<SuccessResponse<OrganizationResponse>> {
    return this.http
      .get<SuccessResponse<OrganizationResponse>>(
        `${this.baseUrl}/${companyId}/Organization/default`,
        {},
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Generate new SDK authentication keys for an organization.
   * @deprecated Use generateAPIKey instead
   * @param companyId - The company ID
   * @returns Observable of SuccessResponse containing GenerateSDKKeysResponse.
   */
  generateSDKKeys(companyId: string): Observable<SuccessResponse<GenerateSDKKeysResponse>> {
    return this.http
      .post<SuccessResponse<GenerateSDKKeysResponse>>(
        `${this.baseUrl}/${companyId}/Organization/generate-sdk-keys`,
        {},
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Generate new API key for an organization (new Bearer auth format).
   * @param companyId - The company ID
   * @param name - Optional name for the API key
   * @returns Observable of SuccessResponse containing GenerateAPIKeyResponse.
   */
  generateAPIKey(
    companyId: string,
    name?: string,
  ): Observable<SuccessResponse<GenerateAPIKeyResponse>> {
    const payload = name ? { name } : {};
    return this.http
      .post<SuccessResponse<GenerateAPIKeyResponse>>(`${this.baseUrl}/v1/api-keys`, {
        knostos_company_id: companyId,
        ...payload,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Handle API errors and return a consistent error format.
   * @param error - The HTTP error response.
   * @returns Observable of ErrorResponse.
   */
  private handleError(error: any): Observable<never> {
    if (error.error?.error) {
      const apiError: ErrorResponse = error.error;
      const userMessage = ERROR_CODE_MAP[apiError.error.code] || 'An unexpected error occurred.';

      return throwError(() => ({
        ...apiError,
        userMessage, // Attach user-friendly message
      }));
    }

    return throwError(() => ({
      error: {
        code: 'UNEXPECTED_ERROR',
        details: {},
        message: 'An unexpected error occurred.',
      },
      tracking_id: error.tracking_id || 'N/A',
    }));
  }
}
